<template>
  <section id="services" class="py-5 light-section">
    <div class="intro-text text-center mb-5">
      <h2>Unsere Schwerpunkte und Lösungen</h2>
      <p>Wir kombinieren modernste Technologien mit maßgeschneiderten Dienstleistungen, um Ihre Herausforderungen zu lösen.</p>
    </div>

    <div class="container">
      <div class="row">
        <div
            class="col-md-6 col-lg-3 mb-4 slide-in-card"
            :class="{ 'slide-in-card-active': cardsVisible }"
            v-for="theme in themes"
            :key="theme.title"
        >
          <div class="card shadow-sm h-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex align-items-start mb-3">
                <div class="icon-container d-flex justify-content-center align-items-center">
                  <i :class="theme.icon" class="icon"></i>
                </div>
              </div>
              <h3 class="card-title">{{ theme.title }}</h3>
              <p class="card-text flex-grow-1">{{ theme.description }}</p>
              <div class="services-header mt-3">
                <h4 class="mb-0">Ihre Möglichkeiten</h4>
              </div>
              <ul class="list-unstyled mt-3">
                <li v-for="service in theme.services" :key="service.name" class="d-flex align-items-center">
                  <i :class="service.icon" class="service-icon mr-2"></i>
                  <span>{{ service.name }}</span>
                </li>
              </ul>
              <router-link :to="theme.route" class="btn btn-outline-primary mt-3">
                Weitere Informationen
                <i class="fas fa-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';

export default defineComponent({
  setup() {
    const themes = ref([
      {
        title: 'Effizientes Datenmanagement',
        description: 'Strukturiertes Datenmanagement zur Verbesserung der Auffindbarkeit, Verständlichkeit und Datenverfügbarkeit durch umfassende Datenkataloge – für eine schnelle Datenfindung und verkürzte Time-to-Analytics.',
        icon: 'fas fa-database',
        route: { name: 'Datenmanagement' },
        services: [
          { name: 'Aufbau von Datenkatalogen', icon: 'fas fa-folder-open' },
          { name: 'Optimierung Ihrer Datenqualität', icon: 'fas fa-check-circle' },
          { name: 'Verfügbarkeit von Daten sichern und steigern', icon: 'fas fa-search' }
        ],
      },
      {
        title: 'KI und Automatisierung',
        description: 'Automatisierung und optimierte Entscheidungsfindung durch KI zur Steigerung der Effizienz und Effektivität Ihrer Geschäftsprozesse – für eine zukunftsorientierte Prozessoptimierung und datenbasierte Prognosen.',
        icon: 'fas fa-microchip',
        route: { name: 'KI' },
        services: [
          { name: 'Effizientes Datenmanagement', icon: 'fas fa-folder-open' },
          { name: 'Automatisierte Erstellung von Berichten', icon: 'fas fa-file-export' },
          { name: 'Intelligente Datenanalyse und KI-Agenten', icon: 'fas fa-lightbulb' }
        ],
      },
      {
        title: 'LLM-basiertes Wissensmanagement',
        description: 'Steigern Sie die Effizienz Ihres Wissensmanagements durch den Einsatz von Sprachmodellen wie ChatGPT, um Wissen in Ihrem Unternehmen intelligent zu organisieren, zugänglich zu machen und nutzbar zu gestalten.',
        icon: 'fas fa-brain',
        route: { name: 'LLM' },
        services: [
          { name: 'Wissen intelligent mit KI-Unterstützung organisieren', icon: 'fas fa-book' },
          { name: 'Automatisierte Erstellung von Berichten', icon: 'fas fa-file-alt' },
          { name: 'Kundenkommunikation proaktiv optimieren', icon: 'fas fa-file-alt' },
        ],
      },
      {
        title: 'IoT und Echtzeit-Überwachung',
        description: 'Nutzen Sie IoT-Sensorik um Objekte und Prozesse dauerhaft zu überwachen - auch unterwegs auf dem Smartphone. Die automatisierte Datenerfassung erlaubt es Ihnen, Gefahren rechtzeitig zu erkennen und zu reagieren, bevor Schäden auftreten.',
        icon: 'fas fa-wifi',
        route: { name: 'IoT' },
        services: [
          { name: 'Systemüberwachung - jederzeit und überall', icon: 'fas fa-network-wired' },
          { name: 'Neue Informationsquellen nutzen', icon: 'fas fa-tachometer-alt' },
          { name: 'Digitalisierung Ihrer bestehenden Infrastruktur', icon: 'fas fa-rocket' }
        ],
      }
    ]);

    const cardsVisible = ref(false);

    onMounted(() => {
      const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                cardsVisible.value = true;
                observer.disconnect(); // Stop observing after the first appearance
              }
            });
          },
          { threshold: 0.1 }
      );

      const cardsSection = document.querySelector('#services .container');
      if (cardsSection) observer.observe(cardsSection);
    });

    return { themes, cardsVisible };
  },
});
</script>

<style scoped lang="less">
@import '@/assets/style.less';
/* Stil für die Überschrift und den Einleitungstext */
.intro-text h2 {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3a3a3a;
}

.intro-text p {
  font-size: 1.2rem;
  color: #555;
}

ul {
  padding-left: 0;
  list-style-type: none;
}

.service-icon {
  font-size: 1.2rem;
  color: @primary-color;
  width: 24px;
  height: 24px;
  display: inline-block;
  text-align: center;
  line-height: 24px;
  margin-right: 10px;
}

li {
  display: flex;
  align-items: center;
  margin-bottom: 7px;
}

.services-header h4 {
  font-size: 1.25rem;
  color: #3a3a3a;
  margin-bottom: 1rem;
}

/* Centered, Slightly Larger Button Styling */
.btn-outline-primary {
  border-color: @primary-color;
  background-color: @primary-color;
  color: @text-color-dark;
  border: none;
  border-radius: 50px;
  padding: 8px 20px; /* Slightly larger padding */
  font-size: 1rem; /* Moderate font size */
  display: inline-flex;
  align-items: center;
  gap: 8px; /* Space between text and icon */
  flex-direction: row; /* Keeps text on left, icon on right */
  justify-content: center;
}

.btn-outline-primary:hover {
  background-color: @accent-color;
  border-color: @accent-color;
}

/* Icon Styling within Button */
.btn-outline-primary i {
  background-color: #ffffff; /* White background for contrast */
  color: #e2a84b; /* Matches button color */
  border-radius: 50%; /* Circular icon background */
  padding: 10px; /* Adjusted padding inside the icon */
  font-size: 1rem; /* Keeps icon size balanced */
}
</style>
